import { PlusCircleIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { GenericTable } from '@/modules/common';
import { StyleItem } from '@/modules/styles/types';
import { TextStyleItem } from '@/modules/text-styles/types';

import { AddEditStyleDialog } from './components/add-edit-style-dialog';
import { AddEditTextStyleDialog } from './components/add-edit-text-style-dialog';
import { useStylesTab } from './hooks/use-styles';
import { useTextStylesTab } from './hooks/use-text-styles';

export type StyleTabItem = Partial<StyleItem> & { styleId: string };
export type TextStyleTabItem = Partial<TextStyleItem> & { textStyleId: string };

export function StylesTab() {
  const [addStyleModalOpen, setAddStyleModalOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<StyleTabItem | null>(null);

  const [addTextStyleModalOpen, setAddTextStyleModalOpen] = useState(false);
  const [selectedTextStyle, setSelectedTextStyle] = useState<TextStyleTabItem | null>(null);

  const {
    formattedStyles,
    columns,
    fields,
    stylesData,
    isFetching,
    onAdd,
    onEdit,
  } = useStylesTab();

  const {
    formattedTextStyles,
    columns: textStyleColumns,
    fields: textStyleFields,
    textStylesData,
    isFetching: isTextStylesFetching,
    onAdd: onAddTextStyle,
    onEdit: onEditTextStyle,
  } = useTextStylesTab();

  return (
    <div className="flex flex-col gap-8">
      <div>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Styles Table</h3>
          <Button
            disabled={isFetching}
            data-testid="add-style-button"
            onClick={() => setAddStyleModalOpen(true)}
            startIcon={<PlusCircleIcon size={16} />}
          >
            Add style
          </Button>
        </div>
        <GenericTable
          name="styles"
          key="styles-table"
          columns={columns}
          data={formattedStyles}
          emptyCellClassName="h-20"
          tableCellClassName="py-1"
          tableRowClassName="hover:bg-transparent"
          emptyTitle="No styles yet"
          emptyDescription="Start building your styles."
          onClick={(item) => {
            setSelectedStyle(item);
            setAddStyleModalOpen(true);
          }}
          disabledColumnsIdsToClick={['name', 'actions']}
          emptyButton={
            <Button
              disabled={isFetching}
              data-testid="add-style-button"
              onClick={() => setAddStyleModalOpen(true)}
              startIcon={<PlusCircleIcon size={16} />}
            >
              Add style
            </Button>
          }
        >
          <p className="text-zinc-400 text-sm">
            All styles: {fields?.length ?? 0}
          </p>
        </GenericTable>
      </div>
      <AddEditStyleDialog
        isOpen={addStyleModalOpen}
        onOpenChange={(open) => {
          setAddStyleModalOpen(open);
          setSelectedStyle(null);
        }}
        stylesData={stylesData}
        selectedStyle={selectedStyle}
        onAdd={onAdd}
        onEdit={(style) => onEdit(style, selectedStyle)}
      />

      <div>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Text Styles Table</h3>
          <Button
            disabled={isTextStylesFetching}
            data-testid="add-text-style-button"
            onClick={() => setAddTextStyleModalOpen(true)}
            startIcon={<PlusCircleIcon size={16} />}
          >
            Add text style
          </Button>
        </div>
        <GenericTable
          name="textStyles"
          key="text-styles-table"
          columns={textStyleColumns}
          data={formattedTextStyles ?? []}
          emptyCellClassName="h-20"
          tableCellClassName="py-1"
          tableRowClassName="hover:bg-transparent"
          emptyTitle="No text styles yet"
          emptyDescription="Start building your text styles."
          onClick={(item) => {
            setSelectedTextStyle(item);
            setAddTextStyleModalOpen(true);
          }}
          disabledColumnsIdsToClick={['name', 'actions']}
          emptyButton={
            <Button
              disabled={isTextStylesFetching}
              data-testid="add-text-style-button"
              onClick={() => setAddTextStyleModalOpen(true)}
              startIcon={<PlusCircleIcon size={16} />}
            >
              Add text style
            </Button>
          }
        >
          <p className="text-zinc-400 text-sm">
            All text styles: {textStyleFields?.length ?? 0}
          </p>
        </GenericTable>
      </div>
      <AddEditTextStyleDialog
        isOpen={addTextStyleModalOpen}
        onOpenChange={(open) => {
          setAddTextStyleModalOpen(open);
          setSelectedTextStyle(null);
        }}
        textStylesData={textStylesData}
        selectedTextStyle={selectedTextStyle}
        onAdd={onAddTextStyle}
        onEdit={(textStyle) => onEditTextStyle(textStyle, selectedTextStyle)}
      />
    </div>
  );
}
